import React, { useState, useCallback } from "react";
import { Container, Button, Card } from "react-bootstrap";
import NavbarComp from "../../shared/components/Navbar";
import { IoAddCircleOutline } from "react-icons/io5";
import AlertMessage from "../../shared/components/Alert";
import { useArtikel } from "./hooks/useArtikel";
import ArtikelModal from "./components/ArtikelModal";
import ArtikelTable from "./components/ArtikelTable";
import PaginationControls from "./components/PaginationControls";
import SearchAndFilter from "./components/SearchAndFilter";
import SortControls from "./components/SortControls";
import ArtikelViewModal from "./components/ArtikelViewModal";
import Swal from "sweetalert2";

const ArtikelPage = () => {
  const {
    artikel,
    loading,
    error,
    alert,
    pagination,
    meta,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    changeLimit,
    changeSort,
    toggleSortOrder,
    search,
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    addArtikel,
    editArtikel,
    removeArtikel,
    updateOnActive,
    currentSortField,
    currentSortOrder,

    // Filter functions
    filters,
    updateStatusFilter,
    updateTypeFilter,
    updateTypeKontenChange,
    clearFilters,
  } = useArtikel();

  const [showModal, setShowModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Gunakan filters dari hook
  const selectedStatus = filters.status;
  const selectedType = filters.type;
  const selectedTypeKonten = filters.typeKonten;

  // Handle modal operations
  const handleOpenModal = (data = null) => {
    setEditingData(data);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingData(null);
  };

  // Handle artikel operations
  const handleSubmitArtikel = async (artikelData) => {
    if (editingData) {
      await editArtikel(editingData.id, artikelData);
    } else {
      await addArtikel(artikelData);
    }
    handleCloseModal();
  };

  const handleDeleteArtikel = (id) => {
    Swal.fire({
      title: "Apakah Anda yakin?",
      text: "Data yang dihapus tidak dapat dikembalikan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await removeArtikel(id);
          Swal.fire("Terhapus!", "Artikel berhasil dihapus.", "success");
          // Opsional: refresh daftar artikel
        } catch (error) {
          Swal.fire(
            "Gagal!",
            "Terjadi kesalahan saat menghapus artikel.",
            "error",
          );
        }
      }
    });
  };

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingArtikel, setViewingArtikel] = useState(null);

  // Handle view artikel
  const handleViewArtikel = useCallback((data) => {
    setViewingArtikel(data);
    setShowViewModal(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewingArtikel(null);
  }, []);
  // Handle search and filter
  const handleSearch = useCallback(
    (e) => {
      e.preventDefault();
      search(searchTerm);
    },
    [searchTerm, search],
  );

  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  const handleStatusChange = useCallback(
    (status) => {
      updateStatusFilter(status);
    },
    [updateStatusFilter],
  );

  const handleTypeChange = useCallback(
    (type) => {
      updateTypeFilter(type);
    },
    [updateTypeFilter],
  );

  const handleTypeKontenChange = useCallback(
    (typeKonten) => {
      updateTypeKontenChange(typeKonten);
    },
    [updateTypeKontenChange],
  );

  const handleClearFilters = useCallback(() => {
    clearFilters();
    setSearchTerm("");
    search("");
  }, [clearFilters, search]);

  // Handle sort
  const handleSort = useCallback(
    (field) => {
      toggleSortOrder(field);
    },
    [toggleSortOrder],
  );

  // Handle pagination
  const handlePageChange = useCallback(
    (page) => {
      goToPage(page);
    },
    [goToPage],
  );

  const handleLimitChange = useCallback(
    (limit) => {
      changeLimit(limit);
    },
    [changeLimit],
  );

  return (
    <>
      <NavbarComp />

      <AlertMessage
        show={alert.show}
        variant={alert.variant}
        message={alert.message}
        onClose={() => {}}
      />

      <Container fluid className="mt-2 mt-md-4 px-3 px-md-4">
        {/* Header Card */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
              <div>
                <h2 className="mb-1">Artikel Management</h2>
                <p className="text-muted mb-0">
                  Total: {meta.totalItems || 0} artikel | Page {pagination.page}{" "}
                  of {meta.totalPages || 1}
                </p>
                {selectedStatus !== "ALL" && (
                  <small className="text-muted">
                    Filtered by:{" "}
                    {selectedStatus !== "ALL" && `Status: ${selectedStatus}`}
                    {selectedStatus !== "ALL" && selectedType !== "ALL" && ", "}
                    {selectedType !== "ALL" && `Type: ${selectedType}`}
                    {selectedTypeKonten !== "ALL" &&
                      selectedTypeKonten &&
                      `Type Post: ${selectedTypeKonten}`}
                  </small>
                )}
              </div>
              <Button
                variant="success"
                className="d-flex align-items-center gap-2 px-4"
                onClick={() => handleOpenModal()}
              >
                <IoAddCircleOutline size={20} />
                Tambah Artikel
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Search and Filter Card */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <SearchAndFilter
              searchTerm={searchTerm}
              selectedStatus={selectedStatus}
              selectedType={selectedType}
              selectedTypeKonten={selectedTypeKonten}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearch}
              onStatusChange={handleStatusChange}
              onTypeChange={handleTypeChange}
              onTypeKontenChange={handleTypeKontenChange}
              onClearFilters={handleClearFilters}
            />
          </Card.Body>
        </Card>

        {/* Sort Controls Card */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <SortControls
              currentSortField={currentSortField}
              currentSortOrder={currentSortOrder}
              onSortChange={handleSort}
            />
          </Card.Body>
        </Card>

        {/* Main Content Card */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            {/* Artikel Table */}
            <ArtikelTable
              artikel={artikel}
              loading={loading}
              error={error}
              pagination={pagination}
              meta={meta}
              currentSortField={currentSortField}
              currentSortOrder={currentSortOrder}
              onSort={handleSort}
              onEdit={handleOpenModal}
              onDelete={handleDeleteArtikel}
              onView={handleViewArtikel}
            />

            {/* Pagination Controls */}
            {!loading && !error && artikel.length > 0 && (
              <PaginationControls
                pagination={pagination}
                meta={meta}
                hasNextPage={hasNextPage}
                hasPreviousPage={hasPreviousPage}
                onPageChange={handlePageChange}
                onLimitChange={handleLimitChange}
                onFirstPage={firstPage}
                onLastPage={lastPage}
                onNextPage={nextPage}
                onPreviousPage={previousPage}
              />
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* Artikel Modal */}
      <ArtikelModal
        show={showModal}
        handleClose={handleCloseModal}
        handleSubmit={handleSubmitArtikel}
        initialData={editingData}
      />

      <ArtikelViewModal
        show={showViewModal}
        handleClose={handleCloseViewModal}
        artikel={viewingArtikel}
      />
    </>
  );
};

export default ArtikelPage;
